import { motion } from "framer-motion";
import { ArrowRight, Shield, Clock, FileCheck } from "lucide-react";
import { Button } from "./ui/button";

const HeroSection = () => {
  const scrollToDemo = (e) => {
    e.preventDefault();
    const element = document.querySelector("#book-demo");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const trustBadges = [
    { icon: Shield, label: "100% Data Sovereignty" },
    { icon: Clock, label: "Zero Learning Curve" },
    { icon: FileCheck, label: "Audit-Ready Files" },
  ];

  return (
    <section
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-1/2 h-full opacity-5 pointer-events-none">
        <div className="absolute top-20 right-20 w-96 h-96 rounded-full bg-navy blur-3xl" />
        <div className="absolute bottom-40 right-40 w-64 h-64 rounded-full bg-gold blur-2xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-32 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Overline */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="font-body text-sm font-semibold uppercase tracking-widest text-gold mb-6"
              data-testid="hero-overline"
            >
              For CA Firms Drowning in WhatsApp
            </motion.p>

            {/* Main Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-navy leading-tight tracking-tight mb-6"
              data-testid="hero-headline"
            >
              Stop the WhatsApp Chaos.
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="font-body text-base md:text-lg text-slate-muted leading-relaxed mb-8 max-w-xl"
              data-testid="hero-subheadline"
            >
              We turn your client's "Good Morning" messages and crumpled receipts 
              into <strong className="text-navy">organized, audit-ready files</strong>—automatically. 
              No new dashboards to learn. No data on third-party servers. 
              Just a clean Google Drive and peace of mind.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 mb-12"
            >
              <Button
                onClick={scrollToDemo}
                className="cta-primary flex items-center gap-2 justify-center"
                data-testid="hero-cta-primary"
              >
                See the Magic
                <ArrowRight size={18} />
              </Button>
              <Button
                variant="outline"
                onClick={scrollToDemo}
                className="font-body font-semibold tracking-wide uppercase text-sm px-8 py-4 rounded-full border-2 border-navy/20 text-navy hover:bg-navy hover:text-white transition-all duration-300"
                data-testid="hero-cta-secondary"
              >
                Book a 15-Min Demo
              </Button>
            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="flex flex-wrap gap-6"
            >
              {trustBadges.map((badge, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 text-slate-muted"
                  data-testid={`trust-badge-${index}`}
                >
                  <badge.icon size={18} className="text-gold" />
                  <span className="font-body text-sm font-medium">
                    {badge.label}
                  </span>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Visual */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative hidden lg:block"
          >
            <div className="relative">
              {/* Main Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-[0_25px_80px_rgba(0,0,0,0.12)]">
                <img
                  src="https://images.unsplash.com/photo-1511385348-a52b4a160dc2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMG1pbmltYWxpc3QlMjB3aGl0ZSUyMG9mZmljZSUyMGRlc2slMjBsYXB0b3AlMjBwbGFudHxlbnwwfHx8fDE3Njk3OTg3MTd8MA&ixlib=rb-4.1.0&q=85"
                  alt="Clean minimalist office setup"
                  className="w-full h-auto object-cover"
                  data-testid="hero-image"
                />
              </div>

              {/* Floating Card - Document Filed */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1 }}
                className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-[0_15px_50px_rgba(0,0,0,0.1)] border border-black/5"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <FileCheck size={20} className="text-green-600" />
                  </div>
                  <div>
                    <p className="font-body text-xs text-slate-muted">Just Filed</p>
                    <p className="font-body text-sm font-semibold text-navy">
                      Client_Invoice_Dec24.pdf
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Floating Card - Time Saved */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.2 }}
                className="absolute -top-4 -right-4 bg-navy rounded-xl p-4 shadow-[0_15px_50px_rgba(0,0,0,0.2)]"
              >
                <p className="font-body text-xs text-white/70">Weekly Time Saved</p>
                <p className="font-heading text-2xl font-bold text-gold">15+ Hours</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
